import { useState } from 'react';
import { App, Button, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  istDutyDate,
  readableDate,
  UPLOADED_ORDER_SORT_FIELDS,
  type Paginated,
  type UploadedOrderDto,
} from '@pams/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EmptyState } from '@/components/EmptyState';
import { NameCell } from '@/components/NameCell';
import { useServerTable } from '@/hooks/useServerTable';
import { api, type ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';
import { LIBRARY_COPY } from '../copy';
import { PdfViewerModal, type PdfSource } from '@/components/attachments/PdfViewerModal';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C3a §2] THE UPLOADED-ORDER LIBRARY, AS A LIST.
 *
 *  ⚠️ A SECOND `useServerTable` ON ONE PAGE, WITH `urlKeyPrefix` — the hook's own documented
 *  affordance for exactly this, so it needs no change. Ten consumers and still not one line of it
 *  has moved.
 *
 *  ⚠️ THE FIRST COLUMN OPENS THE ROW, `NameCell`'s rule — but it opens a VIEWER rather than
 *  navigating, so it is a button and not an anchor. That is the same exception `AttachmentList`
 *  already makes and for the same reason: the destination is a blob URL, which cannot be
 *  ⌘-clicked, cannot be copied and means nothing in another tab. A control that cannot be opened
 *  in a new tab must not look like one.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const LibraryTable = ({ canWrite }: { canWrite: boolean }): React.ReactElement => {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [viewing, setViewing] = useState<PdfSource | null>(null);

  const table = useServerTable<UploadedOrderDto, (typeof UPLOADED_ORDER_SORT_FIELDS)[number]>({
    sortFields: UPLOADED_ORDER_SORT_FIELDS,
    defaultSortBy: 'orderDate',
    defaultSortOrder: 'desc',
    defaultLimit: 20,
    /* ⚠️ Two tables on one page — the hook's own answer, and why this needs no hook change. */
    urlKeyPrefix: 'lib',
    filterKeys: [],
    queryKey: (params) => queryKeys.uploadedOrders.list(params),
    queryFn: (params, signal) =>
      api.get<Paginated<UploadedOrderDto>>('/uploaded-orders', {
        query: {
          page: params.page,
          limit: params.limit,
          sortBy: params.sortBy,
          sortOrder: params.sortOrder,
        },
        signal,
      }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/uploaded-orders/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.uploadedOrders.all });
      void message.success('Removed');
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const columns: ColumnsType<UploadedOrderDto> = [
    {
      key: 'title',
      title: LIBRARY_COPY.title,
      sorter: true,
      render: (_value, row) => (
        <NameCell
          name={row.title}
          onOpen={() => {
            setViewing({
              /* ⚠️ [C3b §5] THE SAME READER THE ARRANGEMENT ATTACHMENTS USE, on this route. The
                 library's own `OrderViewerModal` was deleted rather than kept beside it: two
                 blob-lifecycle implementations is two places the revoke can go wrong, and the
                 non-obvious half (revoke on CLOSE, the cancelled guard on both arms, the
                 unconditional Download) had already been got right once. */
              path: `/uploaded-orders/${row.id}/file`,
              filename: row.filename,
              title: row.title,
            });
          }}
        />
      ),
    },
    {
      key: 'orderDate',
      title: LIBRARY_COPY.orderDate,
      width: 160,
      sorter: true,
      render: (_value, row) => readableDate(row.orderDate) ?? row.orderDate,
    },
    {
      key: 'filename',
      title: LIBRARY_COPY.file,
      render: (_value, row) => <span className="text-muted-foreground">{row.filename}</span>,
    },
    {
      key: 'createdAt',
      title: 'Added',
      width: 200,
      sorter: true,
      render: (_value, row) => (
        <span className="text-muted-foreground">
          {/*
            ⚠️ `istDutyDate`, NEVER `.slice(0, 10)`. `uploadedAt` is an ISO **instant**, not a
            business date — §7's own distinction — so slicing takes the UTC calendar day. Every
            upload between 00:00 and 05:30 IST would be dated the day before, on a screen whose
            whole subject is when an order was filed. `istDutyDate` is the ONE crossing.
          */}
          {row.uploadedBy?.name ?? '—'} · {readableDate(istDutyDate(row.uploadedAt)) ?? ''}
        </span>
      ),
    },
    {
      key: 'library-actions',
      title: '',
      width: 100,
      align: 'right' as const,
      render: (_value, row) =>
        canWrite ? (
          <Button
            type="text"
            size="small"
            danger
            data-testid={`library-remove-${row.id}`}
            onClick={() => {
              modal.confirm({
                title: LIBRARY_COPY.removeConfirm,
                content: LIBRARY_COPY.removeBody,
                okText: LIBRARY_COPY.remove,
                okButtonProps: { danger: true },
                onOk: () => {
                  remove.mutate(row.id);
                },
              });
            }}
          >
            {LIBRARY_COPY.remove}
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <Table<UploadedOrderDto>
        {...table.tableProps}
        data-testid="library-table"
        columns={columns}
        size="middle"
        /* ⚠️ §8's rule: cells never truncate, tables scroll, and `max-content` is what makes the
           browser overflow rather than compress. Never `x: true`. */
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <EmptyState title={LIBRARY_COPY.emptyTitle} description={LIBRARY_COPY.emptyBody} /> }}
      />
      <PdfViewerModal
        source={viewing}
        onClose={() => {
          setViewing(null);
        }}
      />
    </>
  );
};
